import { useState, type ReactNode } from 'react';
import { ATTENDANCE_STATUSES } from '@ehr/shared';
import { Modal } from './Modal.js';
import { lateText } from '../lib/noteLate.js';
import {
  groupHeading,
  ROSTER_GROUP_LABEL,
  ROSTER_ORDER,
  type RosterLine,
} from '../lib/sessionRoster.js';
export interface RosterEntry {
  contactId: string;
  name: string;
  line: RosterLine;
  /** Tags after the name: Walk-in, Billed, Needs review. */
  tags?: ReactNode;
}

/**
 * ehr#571 round 2 (V22 rosterHTML, V11 attendees): the session's attendees in Needs you / Ready to
 * sign / Pending review (ehr#689) / Signed, each group with its count and each attendee with their
 * line. A click opens them.
 */
export function RosterGroups({
  entries,
  current,
  onPick,
}: {
  entries: RosterEntry[];
  current: string;
  onPick: (contactId: string) => void;
}) {
  return (
    <>
      {ROSTER_ORDER.map((g) => {
        const list = entries.filter((e) => e.line.group === g);
        return list.length ? (
          <div key={g} className="pn-grp-group">
            <span className="pn-grp-group-h">
              {groupHeading(ROSTER_GROUP_LABEL[g], list.length)}
            </span>
            <div className="pn-fam-roster" role="tablist">
              {list.map((e) => (
                <button
                  key={e.contactId}
                  type="button"
                  role="tab"
                  aria-selected={e.contactId === current}
                  className={`pn-fam-who${e.contactId === current ? ' on' : ''}`}
                  onClick={() => onPick(e.contactId)}
                >
                  <b>{e.name}</b>
                  {e.tags}
                  <span className={`pn-fam-st pn-rl-${e.line.tone}`}>{e.line.text}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null;
      })}
    </>
  );
}

type Attendance = (typeof ATTENDANCE_STATUSES)[number];
export interface AttendanceRow {
  contactId: string;
  name: string;
  value: Attendance;
  /** Signed or with the reviewer: shown, not changed. */
  locked: boolean;
}

/**
 * ehr#571 round 2 (GROUP_NOTE_10-20-V22 openAttendance, 46 A; FAMILY_COUPLES_NOTE_10-20-V11):
 * everyone's attendance in one window, before anything is written. Save hands back only the rows
 * that changed.
 */
export function AttendanceWindow({
  rows,
  label = (a) => a,
  busy = false,
  onSave,
  onClose,
}: {
  rows: AttendanceRow[];
  /** A choice's name on screen: a group says "Did not attend" for Not present. */
  label?: (a: Attendance) => string;
  busy?: boolean;
  onSave: (changed: Record<string, Attendance>) => void;
  onClose: () => void;
}) {
  const [picks, setPicks] = useState<Record<string, Attendance>>(() =>
    Object.fromEntries(rows.map((r) => [r.contactId, r.value])),
  );
  const changed = Object.fromEntries(
    rows
      .filter((r) => !r.locked && picks[r.contactId] !== r.value)
      .map((r) => [r.contactId, picks[r.contactId]!]),
  );
  return (
    <Modal
      title="Attendance"
      onClose={onClose}
      className="pn-att-modal"
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={() => onSave(changed)}
          >
            Save
          </button>
        </>
      }
    >
      <div className="pn-att-list">
        {rows.map((r) => (
          <div key={r.contactId} className="pn-att-row">
            <b>{r.name}</b>
            <div className="pn-seg" role="group" aria-label={`${r.name}'s attendance`}>
              {ATTENDANCE_STATUSES.map((a) => (
                <button
                  key={a}
                  type="button"
                  aria-pressed={picks[r.contactId] === a}
                  disabled={r.locked}
                  onClick={() => setPicks((p) => ({ ...p, [r.contactId]: a }))}
                >
                  {label(a)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

/**
 * ehr#571 round 2 (V22 openSignAll): "Sign 3 ready notes?" lists who, asks for the late-entry reason
 * when any of them is late (one reason for all of them, as V22), then the attest line.
 */
export function SignReadyWindow({
  names,
  lateHours,
  supervised,
  busy = false,
  onConfirm,
  onClose,
}: {
  names: string[];
  /** The latest of the late notes' hours after the session; null when none is late. */
  lateHours: number | null;
  /** The notes go to a supervisor (Submit) rather than being signed. */
  supervised: boolean;
  busy?: boolean;
  onConfirm: (lateReason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const n = names.length;
  const verb = supervised ? 'Submit' : 'Sign';
  const go = () => {
    const r = reason.replace(/\s+/g, ' ').trim();
    if (lateHours !== null && !r) return setErr('Add the reason for the late entry.');
    onConfirm(r);
  };
  return (
    <Modal
      title={
        supervised
          ? `Submit ${n} ready note${n === 1 ? '' : 's'} to your supervisor?`
          : `Sign ${n} ready note${n === 1 ? '' : 's'}?`
      }
      onClose={onClose}
      className="pn-signall-modal"
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={go}>
            {verb} {n} note{n === 1 ? '' : 's'}
          </button>
        </>
      }
    >
      <ul className="pn-signall-list">
        {names.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
      {lateHours !== null && (
        <div className="pn-late">
          <b>Late entry · {lateText(lateHours)}</b>
          <label className="pn-late-f">
            <span>Reason for late entry</span>
            <textarea
              value={reason}
              maxLength={2000}
              aria-invalid={!!err || undefined}
              onChange={(e) => {
                setReason(e.target.value);
                setErr('');
              }}
            />
          </label>
          {err && (
            <p className="pn-inline-err" role="alert">
              {err}
            </p>
          )}
        </div>
      )}
      <p className="muted pn-signall-attest">
        By signing, I attest these notes accurately reflect the services provided during this
        session.
      </p>
    </Modal>
  );
}
